export type MetaPageInput = {
    page_id: string;
    countries: string[];
    is_active: boolean;
}

// Which pipeline fetches the page's ads. 'searchapi' is the default; 'meta_loggedin' marks a page
// loaded by the manual Meta Ad Library scrape (typically age-gated), which SearchAPI never fetches.
// Read-only: the brand payload never sends it back.
export type MetaPageFetchSource = 'searchapi' | 'meta_loggedin';

export type MetaPageRecord = {
    page_id: string;
    page_name: string | null;
    countries: string[];
    is_active: boolean;
    last_fetch_date: string | null;
    last_ads_count: number | null;
    fetch_source: MetaPageFetchSource;
}

export type MetaPageValidationResult = {
    exists: boolean;
    pageName: string | null;
    category: string | null;
    activeAdsCount: number | null;
}

export type LinkedinTermInput = {
    search_term: string;
    countries: string[];
    is_active: boolean;
}

export type LinkedinTermRecord = {
    search_term: string;
    countries: string[];
    is_active: boolean;
    last_fetch_date: string | null;
    last_ads_count: number | null;
}

export type Brand = {
    brandKey: string;
    name: string;
    advertiserDomains: string[];
    competingBrands: BrandSummary[];
    logoPath?: string;
    // Written back via the payload's `aiPresenceTopics`; the elaborate list response returns the saved
    // values under `aiTopics`, which the brand drawer reads to prefill in edit mode.
    aiPresenceTopics?: string[];
    aiTopics?: string[];
    metaPages?: MetaPageInput[];
    // Free-text match terms (DEV-583): substrings matched against normalized click URLs / creative
    // OCR text. Client always sends the full current list (empty array = clear); server normalizes.
    clickUrlTerms?: string[];
    ocrTerms?: string[];
    // Alternative display names the LLM may use for this brand (AI Presence attribution)
    aliases?: string[];
    // Cost toggle: when false, this brand's AI topics stop being executed (unless shared)
    aiPresenceEnabled?: boolean;
}

export type BrandPayload = Omit<Brand, 'competingBrands'> & {
    competingBrands: string[];
    aiPresenceTopics?: string[];
    linkedinTerms?: LinkedinTermInput[];
};

export type BrandsResponse = {
    brands: Brand[] | BrandSummary[];
    total: number;
}

export type BrandSummary = {
    name: string;
    brandKey: string;
}

export type ManagementRequest = {
    offset?: number;
    limit?: number;
    elaborate?: boolean;
    term?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
}

export type SortState = {
    sortBy: string;
    sortDir: 'asc' | 'desc';
}

export enum BrandManagementColumn {
    BRAND_KEY = 'brandKey',
    BRAND_NAME = 'name',
    ADVERTISER_DOMAINS = 'advertiserDomains',
    COMPETITORS = 'competingBrands',
    ACCOUNTS = 'accountNames',
    CREATED_AT = 'createdAt',
}

export type UsersBrands = {
    key: string;
    name: string;
}

export type UserBase = {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    verifiedEmail?: boolean;
};

export type User = UserBase & {
    brands: UsersBrands[];
    account: { key: string };
};

export type UserPayload = UserBase & {
    accountKey: string;
};

export type UserResponse = {
    users: User[],
    total: number
}

export enum UserManagementColumn {
    EMAIL = 'email',
    FIRST_NAME = 'firstName',
    LAST_NAME = 'lastName',
    ROLE = 'role',
    CONNECTED_BRANDS = 'brands',
    ACCOUNT = 'account',
    CREATED_AT = 'createdAt',
    LAST_LOGIN = 'lastLogin',
}

export type BrandLogoResponse = {
    logoURL: string;
}

// Single source of truth lives with the user profile (the status arrives on /users/my-user too).
export type { SubscriptionStatus } from '../../shared/types/UserProfile';
import type { SubscriptionStatus } from '../../shared/types/UserProfile';

export type Account = {
    name: string;
    accountKey: string;
    brands: string[];
    userIds: string[];
    createdAt: string;
    // Effective account subscription (EXPIRED already derived server-side). Optional so the
    // screen tolerates a server that predates the trial/subscription redesign.
    subscriptionStatus?: SubscriptionStatus;
    trialEndsAt?: string | null;
}

export type AccountUser = {
    email: string;
    firstName: string;
    lastName: string;
}

export type AccountUsersResponse = {
    accountUsers: AccountUser[];
}

export enum AccountManagementColumn {
    NAME = 'name',
    BRANDS = 'brands',
    USERS = 'userIds',
    CREATED_AT = 'createdAt',
    ACCOUNT_KEY = 'accountKey',
    SUBSCRIPTION = 'subscriptionStatus',
    TRIAL_ENDS_AT = 'trialEndsAt'
}

export type AccountPayload = {
    accountKey: string;
    name: string;
    brandKeys: string[];
}

export type AccountSummary = {
    name: string;
    accountKey: string;
}

export type AccountsResponse = {
    brands: Account[] | AccountSummary[];
    total: number;
}

export type Topic = {
    id: string;
    name: string;
    brandKeys: string[];
    // Derived server-side: attached to at least one AI-presence-enabled brand
    collecting?: boolean;
}

export type AiTopicsResponse = {
  topics: Topic[];
  total: number;
}

// Row shape for the AI Presence topics table: a topic enriched with prompt counts (computed
// client-side from the prompts list — the topics endpoint doesn't know about prompts).
// null counts = the prompts fetch failed (interaction-layer proxy unavailable); rendered as "—".
// managed=false marks a "pipeline only" row: prompts exist under this topic name in Snowflake but
// no console topic matches — surfaced so orphaned prompts stay visible (and deletable).
export type AiTopicRow = Topic & {
  promptCount: number | null;
  managed: boolean;
}

export type AiPrompt = {
    promptId: string;
    topic: string;
    promptText: string;
    createdAt: string;
}

export type AiPromptsPage = {
    data: AiPrompt[];
    page: number;
    limit: number;
    total: number;
    totalPage: number;
}

export type PromptMention = {
    brandKey: string;
    positive: number;
    neutral: number;
    negative: number;
}

export type PromptResultItem = {
    promptId: string;
    promptText: string;
    topic: string;
    date: string;
    responseText: string;
    mentions: PromptMention[];
}

export type PromptResultsResponse = {
    data: PromptResultItem[];
    total: number;
}

export type DryRunResult = {
    responseText: string;
    mentions: Record<string, {positive: number; neutral: number; negative: number}>;
}

export type DomainConflictBrand = {
  brandKey: string;
  name: string;
}

export type DomainConflict = {
  domain: string;
  brands: DomainConflictBrand[];
}

export type DomainConflictsResponse = {
  conflicts: DomainConflict[];
  hasConflicts: boolean;
}

export type CheckDomainConflictsPayload = {
  domains: string[];
  brandKey?: string;
}

export type ResearchRunStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

// One brand-level research request/run (SOCIAL_ADS.BRAND_RESEARCH_RUNS row).
export type ResearchRun = {
  runId: string;
  brandKey: string;
  validFrom: string; // YYYY-MM-DD — estimation covers dates after this
  status: ResearchRunStatus;
  source: 'ON_DEMAND' | 'SCHEDULED';
  requestedBy: string | null;
  error: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

// Latest research snapshot per (channel, country) — channel-aware so LinkedIn
// (and future channels) appear here with no client changes.
export type ResearchSnapshot = {
  channel: string;
  country: string;
  validFrom: string;
  researchedAt: string | null;
}

export type BrandResearchStatus = {
  snapshots: ResearchSnapshot[];
  latestRun: ResearchRun | null;
}

export type ResearchMemory = {
  country: string;
  content: string;
  lastModified: string | null;
}

